// Dock affordances shared by the six HUD panels: the drag grip (dock moves),
// the collapse chevron, the inner-edge resize handle and the rail button a
// collapsed panel becomes. All pointer-based — no drag library.
//
// Sizing/stacking model: each panel renders its own container exactly as
// before (its "bespoke" position classes), and layers the store on top via
// usePanelDockStyle — an inline width/height when the player resized, plus a
// margin shift when the panel lives in a generic dock slot. Panels using a
// bespoke slot never shift, so the untouched defaults stay pixel-identical
// to the pre-dock interface.

import { ChevronDown, ChevronUp, GripVertical } from 'lucide-react';
import {
  type CSSProperties,
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useState,
} from 'react';
import { useT } from '../../i18n/index.js';
import { PANEL_IDS, type PanelDock, type PanelId } from './layout.js';
import { usePanelLayout } from './usePanelLayout.js';

const MD_QUERY = '(min-width: 768px)';

/** Dock styling only applies at md+ — below that the panels keep the phone
    drawer behaviour and the store stays out of the way. */
export function useIsDesktop(): boolean {
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(MD_QUERY).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(MD_QUERY);
    const onChange = (e: MediaQueryListEvent) => setIsDesktop(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);
  return isDesktop;
}

/** Width of a collapsed panel's rail slot (button + breathing room). */
const RAIL_STRIP = '2.5rem';
/** Fallback stack width for an expanded panel with no stored size. */
const NATURAL_WIDTH = 'var(--rail-w)';

/**
 * Inline style a panel's root merges in: the stored size (width for side
 * docks, height for the bottom dock) and — for panels placed in a generic
 * dock slot (`bespoke: false`) — a margin that stacks them behind the dock's
 * other panels instead of on top of them.
 */
export function usePanelDockStyle(id: PanelId, bespoke = true): CSSProperties {
  const entry = usePanelLayout((s) => s.panels[id]);
  const panels = usePanelLayout((s) => s.panels);
  const isDesktop = useIsDesktop();
  if (!isDesktop) return {};
  const style: CSSProperties = {};
  if (entry.size !== null) {
    if (entry.dock === 'bottom') style.height = entry.size;
    else style.width = entry.size;
  }
  if (!bespoke) {
    const siblings = PANEL_IDS.filter((o) => o !== id && panels[o].dock === entry.dock).sort(
      (a, b) => panels[a].order - panels[b].order,
    );
    const parts = siblings.map((o) =>
      panels[o].collapsed
        ? RAIL_STRIP
        : ((panels[o].size ?? NATURAL_WIDTH) as string).replace(/^(\d+)$/, '$1px'),
    );
    if (parts.length > 0) {
      const shift = `calc(${parts.join(' + ')})`;
      if (entry.dock === 'left') style.marginLeft = shift;
      else style.marginRight = shift;
    }
  }
  return style;
}

/** The collapse chevron. A real button with an aria-label (audit + a11y). */
export function PanelCollapseButton({
  id,
  testid,
  className = '',
}: {
  id: PanelId;
  testid?: string;
  className?: string;
}) {
  const t = useT();
  const collapsed = usePanelLayout((s) => s.panels[id].collapsed);
  const toggleCollapsed = usePanelLayout((s) => s.toggleCollapsed);
  return (
    <button
      type="button"
      data-testid={testid}
      aria-label={t(collapsed ? 'layout.expandPanel' : 'layout.collapsePanel')}
      aria-expanded={!collapsed}
      title={t(collapsed ? 'layout.expandPanel' : 'layout.collapsePanel')}
      onClick={() => toggleCollapsed(id)}
      className={`cursor-pointer rounded p-0.5 text-muted-foreground hover:text-foreground ${className}`}
    >
      {collapsed ? <ChevronUp className="size-3.5" /> : <ChevronDown className="size-3.5" />}
    </button>
  );
}

/** Where a pointer lands: a dock edge, or null for the middle (no move). */
function dockZoneFor(x: number, y: number): PanelDock | null {
  const w = window.innerWidth;
  const h = window.innerHeight;
  if (y > h * 0.65) return 'bottom';
  if (x < w * 0.28) return 'left';
  if (x > w * 0.72) return 'right';
  return null;
}

/**
 * The drag handle. Dragging toward an edge and releasing there moves the
 * panel to that dock; releasing near where it started is a no-op. Highlighted
 * while a drop would change the dock.
 */
export function PanelDockGrip({
  id,
  className = '',
}: {
  id: PanelId;
  className?: string;
}) {
  const t = useT();
  const dock = usePanelLayout((s) => s.panels[id].dock);
  const setDock = usePanelLayout((s) => s.setDock);
  const [target, setTarget] = useState<PanelDock | null>(null);

  const onPointerDown = (e: ReactPointerEvent<HTMLButtonElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    let engaged = false;
    let drop: PanelDock | null = null;
    const move = (ev: globalThis.PointerEvent) => {
      if (!engaged && Math.hypot(ev.clientX - startX, ev.clientY - startY) < 6) return;
      engaged = true;
      drop = dockZoneFor(ev.clientX, ev.clientY);
      setTarget(drop === dock ? null : drop);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      setTarget(null);
      if (engaged && drop !== null && drop !== dock) setDock(id, drop);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
  };

  return (
    <button
      type="button"
      aria-label={t('layout.movePanel')}
      title={t('layout.movePanel')}
      onPointerDown={onPointerDown}
      className={`cursor-pointer touch-none rounded p-0.5 text-muted-foreground/70 hover:text-foreground ${className}`}
    >
      <GripVertical className={`size-3.5 ${target !== null ? 'text-primary' : ''}`} aria-hidden />
    </button>
  );
}

/**
 * The inner-edge resize handle: right edge for a left-docked panel, left edge
 * for the right dock, top edge for the bottom dock. Live drags skip the
 * localStorage write; the release commits.
 */
export function PanelResizeHandle({ id, side }: { id: PanelId; side: 'left' | 'right' | 'top' }) {
  const t = useT();
  const setSize = usePanelLayout((s) => s.setSize);

  const onPointerDown = (e: ReactPointerEvent<HTMLDivElement>) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const panel = e.currentTarget.parentElement;
    const rect = panel?.getBoundingClientRect();
    const startSize = side === 'top' ? (rect?.height ?? 0) : (rect?.width ?? 0);
    let last = startSize;
    const move = (ev: globalThis.PointerEvent) => {
      // Right edge grows eastward; a left edge grows westward; a top edge
      // grows upward (bottom-dock height).
      const delta =
        side === 'right'
          ? ev.clientX - e.clientX
          : side === 'left'
            ? e.clientX - ev.clientX
            : e.clientY - ev.clientY;
      last = startSize + delta;
      setSize(id, last, false);
    };
    const up = () => {
      window.removeEventListener('pointermove', move);
      setSize(id, last, true);
    };
    window.addEventListener('pointermove', move);
    window.addEventListener('pointerup', up, { once: true });
  };

  const geometry =
    side === 'top'
      ? 'inset-x-2 top-0 h-1.5 cursor-row-resize'
      : side === 'right'
        ? 'inset-y-2 right-0 w-1.5 cursor-ew-resize'
        : 'inset-y-2 left-0 w-1.5 cursor-ew-resize';

  // Keyboard parity: arrow keys nudge the size by 16px steps (measured from
  // the panel's live box, so it works from the natural size too).
  const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    const grow = side === 'right' ? 'ArrowRight' : side === 'left' ? 'ArrowLeft' : 'ArrowUp';
    const shrink = side === 'right' ? 'ArrowLeft' : side === 'left' ? 'ArrowRight' : 'ArrowDown';
    if (e.key !== grow && e.key !== shrink) return;
    e.preventDefault();
    const rect = e.currentTarget.parentElement?.getBoundingClientRect();
    const current = side === 'top' ? (rect?.height ?? 0) : (rect?.width ?? 0);
    setSize(id, current + (e.key === grow ? 16 : -16), true);
  };

  return (
    <div
      role="separator"
      tabIndex={0}
      aria-orientation={side === 'top' ? 'horizontal' : 'vertical'}
      aria-label={t('layout.resizePanel')}
      title={t('layout.resizePanel')}
      onPointerDown={onPointerDown}
      onKeyDown={onKeyDown}
      className={`absolute z-10 touch-none rounded outline-none transition-colors hover:bg-primary/40 focus-visible:bg-primary/60 ${geometry}`}
    />
  );
}

/**
 * What a collapsed panel becomes: a small icon button on its dock's rail.
 * Stacked by the panel's order among that dock's collapsed panels.
 */
export function PanelRailButton({
  id,
  label,
  icon,
  testid,
}: {
  id: PanelId;
  label: string;
  icon: React.ReactNode;
  testid?: string;
}) {
  const t = useT();
  const entry = usePanelLayout((s) => s.panels[id]);
  const panels = usePanelLayout((s) => s.panels);
  const setCollapsed = usePanelLayout((s) => s.setCollapsed);
  const isDesktop = useIsDesktop();
  if (!isDesktop) return null;
  const index = PANEL_IDS.filter((o) => panels[o].dock === entry.dock && panels[o].collapsed)
    .sort((a, b) => panels[a].order - panels[b].order)
    .indexOf(id);
  const chrome =
    'fixed z-40 flex size-8 items-center justify-center rounded-md border border-border/60 bg-card/95 text-muted-foreground shadow-md backdrop-blur hover:text-foreground cursor-pointer';
  if (entry.dock === 'bottom') {
    return (
      <button
        type="button"
        data-testid={testid}
        aria-label={t('layout.expandPanel')}
        title={label}
        onClick={() => setCollapsed(id, false)}
        className={`${chrome} bottom-4`}
        style={{ right: `calc(0.75rem + ${index * 2.5}rem)` }}
      >
        {icon}
      </button>
    );
  }
  return (
    <button
      type="button"
      data-testid={testid}
      aria-label={t('layout.expandPanel')}
      title={label}
      onClick={() => setCollapsed(id, false)}
      className={`${chrome} ${entry.dock === 'left' ? 'left-1.5' : 'right-1.5'}`}
      style={{ top: `calc(var(--hud-panel-top) + ${index * 2.5}rem)` }}
    >
      {icon}
    </button>
  );
}
